import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { cva } from 'class-variance-authority';

type OlProps = {
  as: 'ol';
} & React.ComponentProps<'ol'>;

type UlProps = {
  as?: 'ul';
} & React.ComponentProps<'ul'>;

export function List(listProps: OlProps | UlProps) {
  const { as = 'ul', className, ...props } = listProps;
  if (as === 'ol') {
    return <ol data-state="list-ol" className={cn('group/list bg-background divide-y overflow-hidden rounded-md border', className)} {...(props as React.ComponentProps<'ol'>)} />;
  }

  return <ul data-state="list-ul" className={cn('group/list bg-background divide-y overflow-hidden rounded-md border', className)} {...(props as React.ComponentProps<'ul'>)} />;
}

export const listElementVariants = cva(
  'group/item text-foreground bg-background hover:bg-accent/80 focus-visible:bg-accent text-foreground transition-outline outline-focus-fing [&_svg]:text-muted-foreground relative inline-flex w-full items-center overflow-hidden p-2 whitespace-nowrap opacity-100 outline-0 transition-colors duration-150 ease-out focus-visible:outline-3 disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      size: {
        default: 'h-13.5 min-h-13.5 gap-2 [&_svg]:size-4',
        double: 'h-14.5 min-h-14.5 gap-3 [&_svg]:size-5',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
);

export function ListItem(listItemProps: ComponentProps<'li'> & VariantProps<typeof listElementVariants>) {
  const { className, size, ...props } = listItemProps;
  return <li data-slot="list-item" tabIndex={0} className={cn(listElementVariants({ size }), className)} {...props} />;
}

export function ListItemContent(listItemContentProps: ComponentProps<'div'>) {
  const { className, ...props } = listItemContentProps;
  return <div data-slot="list-item-content" className={cn('flex grow flex-col gap-0.5', className)} {...props} />;
}

export function ListItemTitle(listItemTitleProps: ComponentProps<'div'>) {
  const { className, ...props } = listItemTitleProps;
  return <div data-state="list-item-title" className={cn('text-foreground truncate text-sm whitespace-nowrap', className)} {...props} />;
}

export function ListItemDescription(listItemDescriptionProps: ComponentProps<'div'>) {
  const { className, ...props } = listItemDescriptionProps;
  return <div data-state="list-item-description" className={cn('text-muted-foreground truncate text-xs whitespace-nowrap', className)} {...props} />;
}
